<template>
  <main class="finance-dashboard">
    <header class="dashboard-intro">
      <a
        class="dashboard-intro__whatsapp"
        href="https://wa.me/5511954361875"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Conversar com a IA no WhatsApp para anotar suas finanças (abre em nova aba)"
        title="Anotar com a IA no WhatsApp"
      >
        <i class="ph ph-whatsapp-logo" aria-hidden="true"></i>
      </a>

      <div class="dashboard-intro__actions">
        <FinancialPrivacyButton />

        <button
          class="refresh-button"
          type="button"
          :disabled="loading"
          aria-label="Atualizar dados do dashboard"
          title="Atualizar dados"
          @click="reload"
        >
          <i class="ph ph-arrow-clockwise" :class="{ 'is-spinning': loading }" aria-hidden="true"></i>
        </button>
      </div>
    </header>

    <section v-if="error" class="dashboard-feedback dashboard-feedback--error" role="alert">
      <div>
        <i class="ph ph-warning-circle" aria-hidden="true"></i>
        <div>
          <strong>Não foi possível atualizar os dados.</strong>
          <span>{{ error }}</span>
        </div>
      </div>
      <button type="button" @click="reload">Tentar novamente</button>
    </section>

    <template v-if="summaryInitializing">
      <section class="summary-card summary-card--loading" aria-label="Carregando resumo financeiro">
        <span class="skeleton skeleton--label"></span>
        <span class="skeleton skeleton--value"></span>
        <span class="skeleton skeleton--chip"></span>
      </section>

      <div class="quick-actions quick-actions--loading" aria-hidden="true">
        <span v-for="item in 3" :key="item" class="skeleton skeleton--action"></span>
      </div>
    </template>

    <template v-else>
      <section class="summary-card" :class="{ 'summary-card--cashflow': summaryMode === 'cashflow' }" aria-labelledby="month-summary-title">
        <div class="summary-card__topline">
          <span id="month-summary-title">{{ monthSummaryTitle }}</span>
          <label class="summary-card__filter summary-card__filter--top">
            <span>Visualizar</span>
            <select v-model="summaryMode" aria-label="Escolher valores exibidos no resumo">
              <option value="cashflow">Entradas e saídas</option>
              <option value="total">Cenário: gastos + previsões</option>
              <option value="expenses">Gastos</option>
              <option value="fixed">Recorrentes</option>
            </select>
          </label>
        </div>

        <div v-if="summaryMode === 'cashflow'" class="summary-card__flow" aria-label="Entradas e saídas do mês">
          <div>
            <small>Entradas</small>
            <strong class="summary-card__flow-value summary-card__flow-value--income">
              {{ privateValue(currentMonthIncomeValue) }}
            </strong>
          </div>
          <div class="summary-card__flow-outflow">
            <small>Saídas</small>
            <strong class="summary-card__flow-value" :class="valuesVisible ? currentMonthOutflowClass : 'summary-card__flow-value--outflow'">
              {{ privateValue(currentMonthOutflowValue) }}
            </strong>
          </div>
        </div>
        <strong v-else class="summary-card__value">{{ privateValue(currentMonthValue) }}</strong>

        <div v-if="summaryMode === 'cashflow'" class="summary-card__balance">
          <div>
            <small>Saldo do mês</small>
            <span>Entradas menos saídas</span>
          </div>
          <strong :class="valuesVisible ? currentMonthBalanceClass : 'is-hidden'">{{ privateValue(currentMonthBalanceValue) }}</strong>
        </div>

        <div class="summary-card__comparison" :class="comparisonClass">
          <i :class="valuesVisible ? comparisonIcon : 'ph ph-eye-slash'" aria-hidden="true"></i>
          <span>{{ valuesVisible ? comparisonText : 'Comparativo oculto' }}</span>
        </div>
      </section>

      <nav class="quick-actions" aria-label="Ações rápidas">
        <router-link :to="{ path: '/financial-management', query: { new: 'expense' } }" class="quick-action quick-action--primary">
          <span class="quick-action__icon"><i class="ph ph-plus" aria-hidden="true"></i></span>
          <span>Novo gasto</span>
        </router-link>

        <router-link to="/financial-management" class="quick-action">
          <span class="quick-action__icon"><i class="ph ph-receipt" aria-hidden="true"></i></span>
          <span>Lançamentos</span>
        </router-link>

        <router-link to="/recurring-expenses" class="quick-action">
          <span class="quick-action__icon"><i class="ph ph-calendar-check" aria-hidden="true"></i></span>
          <span>Recorrentes</span>
        </router-link>
      </nav>

      <div class="dashboard-columns">
        <section class="dashboard-panel category-panel" aria-labelledby="category-title">
          <div class="panel-heading">
            <div>
              <span class="panel-heading__eyebrow">{{ categoryPanelEyebrow }}</span>
              <h2 id="category-title">{{ categoryPanelTitle }}</h2>
            </div>
            <router-link :to="categoryDetailsRoute" class="panel-heading__action">
              <span>Ver tudo</span>
              <i class="ph ph-arrow-right" aria-hidden="true"></i>
            </router-link>
          </div>

          <template v-if="categoryPreview.length">
            <div class="category-list mt-30">
              <article
                v-for="item in categoryPreview"
                :key="item.key"
                class="category-item"
                role="button"
                tabindex="0"
                :aria-label="`Ver gastos de ${item.category}`"
                @click="openCategoryDetails(item)"
                @keydown.enter="openCategoryDetails(item)"
                @keydown.space.prevent="openCategoryDetails(item)"
              >
                <div class="category-item__content">
                  <span class="category-item__marker" :style="{ backgroundColor: categoryColor(item) }" aria-hidden="true"></span>
                  <div class="category-item__name">
                    <div class="category-item__title">
                      <strong>{{ item.category }}</strong>
                      <span v-if="categoryFixedBadge(item)" class="category-item__fixed-badge">{{ categoryFixedBadge(item) }}</span>
                    </div>
                    <span class="category-item__meta">{{ categoryItemMeta(item) }}</span>
                  </div>
                  <strong class="category-item__value">{{ privateValue(money(item.displayTotal)) }}</strong>
                </div>
                <div class="category-item__track" aria-hidden="true">
                  <span :style="{ width: `${categoryPercentage(item)}%`, backgroundColor: categoryColor(item) }"></span>
                </div>
              </article>
            </div>

            <nav v-if="categoryLastPage > 1" class="category-pagination" aria-label="Paginação das categorias">
              <span class="category-pagination__summary">{{ categoryPaginationSummary }}</span>

              <div class="category-pagination__actions">
                <button type="button" :disabled="categoryPage <= 1" @click="goToCategoryPage(categoryPage - 1)">Anterior</button>
                <button
                  v-for="page in categoryPaginationPages"
                  :key="page"
                  type="button"
                  :class="{ active: page === categoryPage }"
                  :aria-current="page === categoryPage ? 'page' : undefined"
                  @click="goToCategoryPage(page)"
                >
                  {{ page }}
                </button>
                <button type="button" :disabled="categoryPage >= categoryLastPage" @click="goToCategoryPage(categoryPage + 1)">Próxima</button>
              </div>

              <div class="category-pagination__mobile">
                <button type="button" aria-label="Página anterior" :disabled="categoryPage <= 1" @click="goToCategoryPage(categoryPage - 1)">
                  <i class="ph ph-caret-left" aria-hidden="true"></i>
                </button>
                <span>Página {{ categoryPage }} de {{ categoryLastPage }}</span>
                <button type="button" aria-label="Próxima página" :disabled="categoryPage >= categoryLastPage" @click="goToCategoryPage(categoryPage + 1)">
                  <i class="ph ph-caret-right" aria-hidden="true"></i>
                </button>
              </div>
            </nav>
          </template>

          <div v-else class="empty-state">
            <span class="empty-state__icon"><i class="ph ph-chart-donut" aria-hidden="true"></i></span>
            <strong>{{ categoryEmptyTitle }}</strong>
            <p>{{ categoryEmptyDescription }}</p>
            <router-link :to="categoryEmptyRoute">{{ categoryEmptyAction }}</router-link>
          </div>
        </section>

        <section class="period-grid" aria-label="Outros períodos">
          <article
            class="period-card"
            role="button"
            tabindex="0"
            aria-label="Ver gastos desta semana"
            @click="openPeriodDetails('week')"
            @keydown.enter="openPeriodDetails('week')"
            @keydown.space.prevent="openPeriodDetails('week')"
          >
            <span class="period-card__icon period-card__icon--week"><i class="ph ph-calendar-blank" aria-hidden="true"></i></span>
            <div>
              <span>{{ currentWeekTitle }}</span>
              <strong>{{ privateValue(currentWeekValue) }}</strong>
            </div>
          </article>

          <article
            class="period-card"
            role="button"
            tabindex="0"
            aria-label="Ver gastos dos últimos 15 dias"
            @click="openPeriodDetails('last15')"
            @keydown.enter="openPeriodDetails('last15')"
            @keydown.space.prevent="openPeriodDetails('last15')"
          >
            <span class="period-card__icon period-card__icon--days"><i class="ph ph-chart-line-up" aria-hidden="true"></i></span>
            <div>
              <span>{{ last15DaysTitle }}</span>
              <strong>{{ privateValue(last15DaysValue) }}</strong>
            </div>
          </article>
        </section>

        <section class="dashboard-panel recurring-panel" aria-labelledby="recurring-title">
          <div class="panel-heading">
            <div>
              <span class="panel-heading__eyebrow">Compromissos</span>
              <h2 id="recurring-title">Gastos recorrentes</h2>
            </div>
            <router-link to="/recurring-expenses" class="panel-heading__action">
              <span>Gerenciar</span>
              <i class="ph ph-arrow-right" aria-hidden="true"></i>
            </router-link>
          </div>

          <div class="recurring-summary mt-30">
            <span>Previsto no período</span>
            <strong>{{ privateValue(recurringTotal) }}</strong>
          </div>

          <template v-if="recurringPreview.length">
            <div class="recurring-list">
              <article v-for="item in recurringPreview" :key="item.id" class="recurring-item">
                <span class="recurring-item__date">
                  <strong>{{ dueDay(item.next_due_date) }}</strong>
                  <small>{{ dueMonth(item.next_due_date) }}</small>
                </span>
                <div class="recurring-item__name">
                  <strong>{{ item.name }}</strong>
                  <span>{{ item.category?.name || 'Sem categoria' }}</span>
                </div>
                <strong class="recurring-item__value">{{ privateValue(money(item.amount)) }}</strong>
              </article>
            </div>

            <nav v-if="recurringLastPage > 1" class="category-pagination recurring-pagination" aria-label="Paginação dos gastos recorrentes">
              <span class="category-pagination__summary">{{ recurringPaginationSummary }}</span>

              <div class="category-pagination__actions">
                <button type="button" :disabled="recurringPage <= 1" @click="goToRecurringPage(recurringPage - 1)">Anterior</button>
                <button
                  v-for="page in recurringPaginationPages"
                  :key="page"
                  type="button"
                  :class="{ active: page === recurringPage }"
                  :aria-current="page === recurringPage ? 'page' : undefined"
                  @click="goToRecurringPage(page)"
                >
                  {{ page }}
                </button>
                <button type="button" :disabled="recurringPage >= recurringLastPage" @click="goToRecurringPage(recurringPage + 1)">Próxima</button>
              </div>

              <div class="category-pagination__mobile">
                <button type="button" aria-label="Página anterior" :disabled="recurringPage <= 1" @click="goToRecurringPage(recurringPage - 1)">
                  <i class="ph ph-caret-left" aria-hidden="true"></i>
                </button>
                <span>Página {{ recurringPage }} de {{ recurringLastPage }}</span>
                <button type="button" aria-label="Próxima página" :disabled="recurringPage >= recurringLastPage" @click="goToRecurringPage(recurringPage + 1)">
                  <i class="ph ph-caret-right" aria-hidden="true"></i>
                </button>
              </div>
            </nav>
          </template>

          <div v-else class="recurring-empty">
            <span>Nenhum gasto recorrente cadastrado.</span>
            <router-link to="/recurring-expenses">Adicionar recorrente</router-link>
          </div>
        </section>
        <MonthlyReportExportCard />
      </div>
    </template>

    <div v-if="selectedCategory" class="category-detail" role="dialog" aria-modal="true" aria-labelledby="category-detail-title">
      <div class="category-detail__backdrop" @click="closeCategoryDetails"></div>
      <section class="category-detail__content">
        <div class="category-detail__sticky-header">
          <span class="category-detail__handle" aria-hidden="true"></span>
          <header class="category-detail__header">
            <div>
              <span class="panel-heading__eyebrow">{{ categoryDetailEyebrow }}</span>
              <h2 id="category-detail-title">{{ selectedCategory.category }}</h2>
            </div>
            <button type="button" aria-label="Fechar detalhes da categoria" @click="closeCategoryDetails">
              <i class="ph ph-x" aria-hidden="true"></i>
            </button>
          </header>
        </div>

        <div class="category-detail__summary">
          <span :style="{ backgroundColor: categoryColor(selectedCategory) }" aria-hidden="true"></span>
          <div>
            <small>Total exibido</small>
            <strong>{{ privateValue(money(selectedCategory.displayTotal)) }}</strong>
            <p class="category-detail__projection">
              Estimativa em 12 meses: <b>{{ privateValue(money(selectedCategory.displayTotal * 12)) }}</b>
            </p>
          </div>
        </div>

        <div v-if="categoryDetailLoading" class="category-detail__loading" role="status">
          <i class="ph ph-spinner-gap is-spinning" aria-hidden="true"></i>
          <span>Carregando os gastos...</span>
        </div>

        <div v-else-if="categoryDetailError" class="category-detail__error" role="alert">
          <span>{{ categoryDetailError }}</span>
          <button type="button" @click="reloadCategoryDetails">Tentar novamente</button>
        </div>

        <div v-else-if="categoryDetailItems.length" class="category-detail__list">
          <article
            v-for="item in categoryDetailItems"
            :key="item.key"
            class="category-detail__item"
            :class="{ 'is-clickable': item.transaction }"
            :role="item.transaction ? 'button' : undefined"
            :tabindex="item.transaction ? 0 : undefined"
            @click="openTransactionDetails(item)"
            @keydown.enter="openTransactionDetails(item)"
            @keydown.space.prevent="openTransactionDetails(item)"
          >
            <span class="category-detail__icon" :class="`is-${item.kind}`">
              <i :class="item.kind === 'fixed' ? 'ph ph-calendar-check' : 'ph ph-arrow-up-right'" aria-hidden="true"></i>
            </span>
            <div>
              <strong>{{ item.title }}</strong>
              <span>{{ item.meta }}</span>
            </div>
            <strong>{{ privateValue(money(item.amount)) }}</strong>
          </article>
        </div>

        <div v-else class="category-detail__empty">
          <i class="ph ph-receipt" aria-hidden="true"></i>
          <strong>Nenhum item encontrado</strong>
          <span>Não há detalhes disponíveis para esta categoria no período.</span>
        </div>
      </section>
    </div>

    <div v-if="selectedPeriod" class="category-detail" role="dialog" aria-modal="true" aria-labelledby="period-detail-title">
      <div class="category-detail__backdrop" @click="closePeriodDetails"></div>
      <section class="category-detail__content">
        <div class="category-detail__sticky-header">
          <span class="category-detail__handle" aria-hidden="true"></span>
          <header class="category-detail__header">
            <div>
              <span class="panel-heading__eyebrow">{{ periodDetailEyebrow }}</span>
              <h2 id="period-detail-title">{{ selectedPeriod.title }}</h2>
            </div>
            <button type="button" aria-label="Fechar detalhes do período" @click="closePeriodDetails">
              <i class="ph ph-x" aria-hidden="true"></i>
            </button>
          </header>
        </div>

        <div class="category-detail__summary">
          <span class="period-detail__marker" aria-hidden="true"></span>
          <div>
            <small>Total exibido</small>
            <strong>{{ privateValue(money(selectedPeriod.total)) }}</strong>
          </div>
        </div>

        <div v-if="periodDetailLoading" class="category-detail__loading" role="status">
          <i class="ph ph-spinner-gap is-spinning" aria-hidden="true"></i>
          <span>Carregando os gastos...</span>
        </div>

        <div v-else-if="periodDetailError" class="category-detail__error" role="alert">
          <span>{{ periodDetailError }}</span>
          <button type="button" @click="reloadPeriodDetails">Tentar novamente</button>
        </div>

        <div v-else-if="periodDetailItems.length" class="category-detail__list">
          <article
            v-for="item in periodDetailItems"
            :key="item.key"
            class="category-detail__item"
            :class="{ 'is-clickable': item.transaction }"
            :role="item.transaction ? 'button' : undefined"
            :tabindex="item.transaction ? 0 : undefined"
            @click="openTransactionDetails(item)"
            @keydown.enter="openTransactionDetails(item)"
            @keydown.space.prevent="openTransactionDetails(item)"
          >
            <span class="category-detail__icon" :class="`is-${item.kind}`">
              <i :class="item.kind === 'fixed' ? 'ph ph-calendar-check' : 'ph ph-arrow-up-right'" aria-hidden="true"></i>
            </span>
            <div>
              <strong>{{ item.title }}</strong>
              <span>{{ item.meta }}</span>
            </div>
            <strong>{{ privateValue(money(item.amount)) }}</strong>
          </article>
        </div>

        <div v-else class="category-detail__empty">
          <i class="ph ph-receipt" aria-hidden="true"></i>
          <strong>Nenhum item encontrado</strong>
          <span>Não há detalhes disponíveis para este período.</span>
        </div>
      </section>
    </div>

    <div v-if="selectedTransaction" class="transaction-detail" role="dialog" aria-modal="true" aria-labelledby="transaction-detail-title">
      <div class="transaction-detail__backdrop" @click="closeTransactionDetails"></div>
      <section class="transaction-detail__content">
        <div class="transaction-detail__sticky-header">
          <span class="transaction-detail__handle" aria-hidden="true"></span>
          <header class="transaction-detail__header">
            <div>
              <span class="panel-heading__eyebrow">Detalhes</span>
              <h2 id="transaction-detail-title">Lançamento</h2>
            </div>
            <div class="transaction-detail__header-actions">
              <button
                v-if="!detailEditing && selectedTransaction.can_edit !== false"
                class="transaction-detail__edit-button"
                type="button"
                title="Editar lançamento"
                @click="startTransactionEdit"
              >
                <i class="ph ph-pencil-simple" aria-hidden="true"></i>
                <span>Editar</span>
              </button>
              <button type="button" aria-label="Fechar detalhes do lançamento" @click="closeTransactionDetails">
                <i class="ph ph-x" aria-hidden="true"></i>
              </button>
            </div>
          </header>
        </div>

        <form v-if="detailEditing" class="transaction-detail__edit-form" @submit.prevent="saveTransactionEdit">
          <label class="transaction-detail__amount-field">
            <span>Valor</span>
            <div>
              <span>R$</span>
              <input
                v-model="detailAmountInput"
                type="text"
                inputmode="numeric"
                autocomplete="off"
                placeholder="0,00"
                aria-label="Valor do lançamento"
                required
                @input="sanitizeDetailAmountInput"
                @paste.prevent="handleDetailAmountPaste"
              />
            </div>
          </label>

          <label v-if="selectedTransaction.type === 'expense'" class="transaction-detail__form-field">
            <span>Categoria</span>
            <select v-model="detailForm.category_id" class="form-select">
              <option :value="null">Outros (automático)</option>
              <option v-for="category in financeCategories" :key="category.id" :value="category.id">{{ category.name }}</option>
            </select>
          </label>

          <label class="transaction-detail__form-field">
            <span>Descrição</span>
            <textarea
              v-model="detailForm.description"
              class="form-control"
              rows="3"
              maxlength="255"
              placeholder="Descreva este lançamento"
            ></textarea>
          </label>

          <dl class="transaction-detail__grid transaction-detail__grid--metadata" aria-label="Informações não editáveis">
            <div aria-disabled="true">
              <dt>Data e hora</dt>
              <dd>{{ formatDetailDate(selectedTransaction.occurred_at) }}</dd>
            </div>
            <div aria-disabled="true">
              <dt>Origem</dt>
              <dd>{{ sourceLabel(selectedTransaction.source) }}</dd>
            </div>
          </dl>

          <div class="transaction-detail__edit-actions">
            <button type="button" :disabled="detailSaving" @click="cancelTransactionEdit">Cancelar</button>
            <button class="is-primary" type="submit" :disabled="detailSaving">
              <span>{{ detailSaving ? 'Salvando...' : 'Salvar alterações' }}</span>
              <i v-if="!detailSaving" class="ph ph-check" aria-hidden="true"></i>
            </button>
          </div>

          <div v-if="detailFeedback" class="transaction-detail__feedback" role="alert">{{ detailFeedback }}</div>
        </form>

        <template v-else>
          <div class="transaction-detail__amount" :class="`is-${selectedTransaction.type || 'expense'}`">
            <span :style="transactionDetailIconStyle(selectedTransaction)">
              <i :class="selectedTransaction.type === 'income' ? 'ph ph-arrow-down-left' : 'ph ph-arrow-up-right'" aria-hidden="true"></i>
            </span>
            <div>
              <small>{{ selectedTransaction.type === 'income' ? 'Entrada' : 'Gasto' }}</small>
              <strong>{{ privateValue(`${selectedTransaction.type === 'income' ? '+' : '-'}${money(selectedTransaction.amount)}`) }}</strong>
            </div>
          </div>

          <dl class="transaction-detail__grid">
            <div>
              <dt>Descrição</dt>
              <dd>{{ selectedTransaction.description?.trim() || 'Sem descrição' }}</dd>
            </div>
            <div>
              <dt>Categoria</dt>
              <dd>{{ selectedTransaction.type === 'income' ? 'Não se aplica' : (selectedTransaction.category?.name || 'Sem categoria') }}</dd>
            </div>
            <div>
              <dt>Data e hora</dt>
              <dd>{{ formatDetailDate(selectedTransaction.occurred_at) }}</dd>
            </div>
            <div>
              <dt>Origem</dt>
              <dd>{{ sourceLabel(selectedTransaction.source) }}</dd>
            </div>
          </dl>
        </template>

        <div class="transaction-detail__message" :class="{ 'is-empty': !selectedTransaction.raw_message }">
          <header>
            <span><i class="ph ph-chat-circle-dots" aria-hidden="true"></i></span>
            <div>
              <strong>O que foi dito à IA</strong>
              <small>Mensagem original do lançamento</small>
            </div>
          </header>
          <blockquote v-if="selectedTransaction.raw_message">“{{ selectedTransaction.raw_message }}”</blockquote>
          <p v-else>Este lançamento foi registrado manualmente, sem uma mensagem original.</p>
        </div>

        <div v-if="selectedTransaction.is_recurring" class="transaction-detail__recurring">
          <i class="ph ph-arrows-clockwise" aria-hidden="true"></i>
          Lançamento recorrente
        </div>
      </section>
    </div>
  </main>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import FinancialPrivacyButton from "../../components/Financial/FinancialPrivacyButton.vue";
import MonthlyReportExportCard from "../../components/Dashboard/MonthlyReportExportCard.vue";

interface MoneyPayload {
  raw?: number;
  formatted?: string;
}

interface CategorySummary {
  category_id?: number | null;
  category?: string;
  total_expense?: MoneyPayload;
  transaction_count?: number;
}

interface RecurringExpense {
  id: number;
  name: string;
  amount: number | string;
  frequency?: string;
  next_due_date?: string | null;
  category?: { id?: number; name?: string } | null;
}

interface FinanceTransactionDetail {
  owner?: { name: string };
  can_edit?: boolean;
  id: number;
  amount: number | string;
  type?: "expense" | "income";
  category_id?: number | null;
  category?: { id?: number | null; name?: string | null; color?: string | null } | null;
  description?: string | null;
  raw_message?: string | null;
  occurred_at?: string | null;
  source?: string | null;
  is_recurring?: boolean;
}

type SummaryMode = "cashflow" | "total" | "expenses" | "fixed";
type PeriodKey = "week" | "last15";

interface PeriodSelection {
  key: PeriodKey;
  title: string;
  start: Date;
  end: Date;
  total: number;
}

interface CategoryDisplay {
  key: string;
  categoryId: number | null;
  category: string;
  expenseAmount: number;
  fixedAmount: number;
  transactionCount: number;
  fixedCount: number;
  displayTotal: number;
}

interface CategoryDetailItem {
  key: string;
  title: string;
  meta: string;
  amount: number;
  kind: "expense" | "fixed";
  transaction?: FinanceTransactionDetail;
}

interface FinanceDashboard {
  current_month?: MoneyPayload;
  previous_month?: MoneyPayload;
  last_15_days?: MoneyPayload;
  current_week?: MoneyPayload;
  month_comparison?: {
    difference?: MoneyPayload;
    percentage?: number | null;
  };
  categories?: CategorySummary[];
  recurring_expenses?: {
    total?: MoneyPayload;
    items?: RecurringExpense[];
  };
}

export default defineComponent({
  name: "DashboardPage",
  components: { FinancialPrivacyButton, MonthlyReportExportCard },
  setup() {
    const store = useStore();
    const categoryColors = ["#06b48a", "#5f6df2", "#f2a93b", "#ef6a67", "#25a0c7"];
    const summaryModeStorageKey = `fin.dashboard.summary-mode.${store.getters.getId || "default"}`;
    const allowedSummaryModes: SummaryMode[] = ["cashflow", "total", "expenses", "fixed"];
    let initialSummaryMode: SummaryMode = "cashflow";
    try {
      const storedSummaryMode = window.localStorage.getItem(summaryModeStorageKey) as SummaryMode | null;
      if (storedSummaryMode && allowedSummaryModes.includes(storedSummaryMode)) {
        initialSummaryMode = storedSummaryMode;
      }
    } catch {
      // Mantém os padrões quando o navegador bloqueia o armazenamento local.
    }
    const summaryMode = ref<SummaryMode>(initialSummaryMode);
    store.dispatch("initializeFinanceValuesVisibility", store.getters.getId || "default");
    const valuesVisible = computed<boolean>(() => store.getters.getFinanceValuesVisible !== false);
    const categoryPage = ref(1);
    const categoriesPerPage = 5;
    const recurringPage = ref(1);
    const recurringPerPage = 3;
    const selectedCategory = ref<CategoryDisplay | null>(null);
    const categoryDetailTransactions = ref<FinanceTransactionDetail[]>([]);
    const categoryDetailLoading = ref(false);
    const categoryDetailError = ref("");
    let categoryDetailRequest = 0;
    const selectedPeriod = ref<PeriodSelection | null>(null);
    const periodDetailTransactions = ref<FinanceTransactionDetail[]>([]);
    const periodDetailLoading = ref(false);
    const periodDetailError = ref("");
    let periodDetailRequest = 0;
    const selectedTransaction = ref<FinanceTransactionDetail | null>(null);
    const detailEditing = ref(false);
    const detailSaving = ref(false);
    const detailFeedback = ref("");
    const detailAmountInput = ref("");
    const detailForm = ref({
      category_id: null as number | null,
      description: "",
    });
    const currentMonthIncomeRaw = ref(0);
    const incomeLoaded = ref(false);
    let incomeRequest = 0;

    const dashboard = computed<FinanceDashboard>(() => store.getters.getFinanceDashboard || {});
    const financeCategories = computed(() => store.getters.getFinanceCategories || []);
    const loading = computed<boolean>(() => Boolean(store.getters.getLoadingFinance));
    const error = computed<string>(() => store.getters.getFinanceError || "");
    const recurringTotal = computed(() => dashboard.value.recurring_expenses?.total?.formatted || money(0));
    const recurringItems = computed(() => dashboard.value.recurring_expenses?.items || []);
    const recurringLastPage = computed(() => Math.max(1, Math.ceil(recurringItems.value.length / recurringPerPage)));
    const recurringPreview = computed(() => {
      const start = (recurringPage.value - 1) * recurringPerPage;
      return recurringItems.value.slice(start, start + recurringPerPage);
    });
    const recurringPaginationPages = computed(() => {
      const start = Math.max(1, recurringPage.value - 2);
      const end = Math.min(recurringLastPage.value, recurringPage.value + 2);
      return Array.from({ length: end - start + 1 }, (_, index) => start + index);
    });
    const recurringPaginationSummary = computed(() => {
      const total = recurringItems.value.length;
      const from = ((recurringPage.value - 1) * recurringPerPage) + 1;
      const to = Math.min(total, recurringPage.value * recurringPerPage);
      return `${from}–${to} de ${total} compromissos`;
    });
    const hasDashboardData = computed(() => {
      return Number(dashboard.value.current_month?.raw || 0) > 0
        || Boolean(dashboard.value.categories?.length)
        || Boolean(dashboard.value.recurring_expenses?.items?.length);
    });
    const summaryInitializing = computed(() => !incomeLoaded.value || (loading.value && !hasDashboardData.value));
    const currentMonthLabel = computed(() => new Intl.DateTimeFormat("pt-BR", { month: "long" }).format(new Date()));
    const summaryModeLabel = computed(() => ({ cashflow: "Gastos realizados", total: "Cenário com previsões", expenses: "Gastos", fixed: "Recorrentes" }[summaryMode.value]));
    const monthSummaryTitle = computed(() => ({
      cashflow: `Entradas e saídas de ${currentMonthLabel.value}`,
      total: `Cenário com previsões de ${currentMonthLabel.value}`,
      expenses: `Gastos lançados em ${currentMonthLabel.value}`,
      fixed: `Recorrentes previstos em ${currentMonthLabel.value}`,
    }[summaryMode.value]));
    const currentWeekTitle = computed(() => `Esta semana · ${summaryModeLabel.value}`);
    const last15DaysTitle = computed(() => `Últimos 15 dias · ${summaryModeLabel.value}`);
    const today = startOfDay(new Date());
    const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const currentMonthEnd = endOfDay(new Date(today.getFullYear(), today.getMonth() + 1, 0));
    const previousMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const previousMonthEnd = endOfDay(new Date(today.getFullYear(), today.getMonth(), 0));
    const currentWeekStart = startOfWeek(today);
    const currentWeekEnd = endOfDay(new Date(currentWeekStart.getFullYear(), currentWeekStart.getMonth(), currentWeekStart.getDate() + 6));
    const last15DaysStart = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 14);
    const categoryItems = computed<CategoryDisplay[]>(() => {
      const categories = new Map<string, CategoryDisplay>();
      const getCategory = (categoryId?: number | null, categoryName?: string) => {
        const name = categoryName || "Sem categoria";
        const key = categoryId ? `id:${categoryId}` : `name:${name}`;
        if (!categories.has(key)) {
          categories.set(key, {
            key,
            categoryId: categoryId || null,
            category: name,
            expenseAmount: 0,
            fixedAmount: 0,
            transactionCount: 0,
            fixedCount: 0,
            displayTotal: 0,
          });
        }
        return categories.get(key) as CategoryDisplay;
      };

      (dashboard.value.categories || []).forEach((category) => {
        const item = getCategory(category.category_id, category.category);
        item.expenseAmount += Number(category.total_expense?.raw || 0);
        item.transactionCount += Number(category.transaction_count || 0);
      });

      recurringItems.value.forEach((recurring) => {
        const amount = recurringAmountForItemMonth(recurring, currentMonthStart, currentMonthEnd);
        if (amount <= 0) return;
        const item = getCategory(recurring.category?.id, recurring.category?.name);
        item.fixedAmount += amount;
        item.fixedCount += 1;
      });

      return Array.from(categories.values())
        .map((category) => ({
          ...category,
          displayTotal: selectedAmount(category.expenseAmount, category.fixedAmount),
        }))
        .filter((category) => category.displayTotal > 0)
        .sort((a, b) => b.displayTotal - a.displayTotal);
    });
    const categoryLastPage = computed(() => Math.max(1, Math.ceil(categoryItems.value.length / categoriesPerPage)));
    const categoryPreview = computed(() => {
      const start = (categoryPage.value - 1) * categoriesPerPage;
      return categoryItems.value.slice(start, start + categoriesPerPage);
    });
    const categoryPaginationPages = computed(() => {
      const start = Math.max(1, categoryPage.value - 2);
      const end = Math.min(categoryLastPage.value, categoryPage.value + 2);
      return Array.from({ length: end - start + 1 }, (_, index) => start + index);
    });
    const categoryPaginationSummary = computed(() => {
      const total = categoryItems.value.length;
      const from = ((categoryPage.value - 1) * categoriesPerPage) + 1;
      const to = Math.min(total, categoryPage.value * categoriesPerPage);
      return `${from}–${to} de ${total} categorias`;
    });
    const categoryDetailEyebrow = computed(() => ({
      cashflow: "Gastos realizados",
      total: "Gastos e compromissos recorrentes",
      expenses: "Lançamentos da categoria",
      fixed: "Compromissos recorrentes da categoria",
    }[summaryMode.value]));
    const categoryDetailItems = computed<CategoryDetailItem[]>(() => {
      const category = selectedCategory.value;
      if (!category) return [];

      const items: CategoryDetailItem[] = [];
      if (summaryMode.value !== "fixed") {
        categoryDetailTransactions.value.forEach((transaction) => {
          items.push({
            key: `expense:${transaction.id}`,
            title: transaction.description?.trim() || "Gasto sem descrição",
            meta: `${sourceLabel(transaction.source)} • ${formatDetailDate(transaction.occurred_at)}`,
            amount: Number(transaction.amount) || 0,
            kind: "expense",
            transaction,
          });
        });
      }

      if (summaryMode.value !== "expenses" && summaryMode.value !== "cashflow") {
        recurringItems.value
          .filter((item) => sameCategory(item, category))
          .forEach((item) => {
            const amount = recurringAmountForItemMonth(item, currentMonthStart, currentMonthEnd);
            if (amount <= 0) return;
            items.push({
              key: `fixed:${item.id}`,
              title: item.name,
              meta: `Recorrente • ${frequencyLabel(item.frequency)}`,
              amount,
              kind: "fixed",
            });
          });
      }

      return items;
    });
    const periodDetailEyebrow = computed(() => ({
      cashflow: "Gastos realizados",
      total: "Gastos e compromissos recorrentes",
      expenses: "Lançamentos do período",
      fixed: "Compromissos recorrentes do período",
    }[summaryMode.value]));
    const periodDetailItems = computed<CategoryDetailItem[]>(() => {
      const period = selectedPeriod.value;
      if (!period) return [];

      const items: CategoryDetailItem[] = [];
      if (summaryMode.value !== "fixed") {
        periodDetailTransactions.value.forEach((transaction) => {
          items.push({
            key: `period-expense:${transaction.id}`,
            title: transaction.description?.trim() || "Gasto sem descrição",
            meta: `${sourceLabel(transaction.source)} • ${formatDetailDate(transaction.occurred_at)}`,
            amount: Number(transaction.amount) || 0,
            kind: "expense",
            transaction,
          });
        });
      }

      if (summaryMode.value !== "expenses" && summaryMode.value !== "cashflow") {
        recurringItems.value.forEach((item) => {
          const occurrences = scheduledOccurrences(item, period.start, period.end);
          const amount = (Number(item.amount) || 0) * occurrences;
          if (amount <= 0) return;
          items.push({
            key: `period-fixed:${item.id}`,
            title: item.name,
            meta: `Recorrente • ${frequencyLabel(item.frequency)}`,
            amount,
            kind: "fixed",
          });
        });
      }

      return items;
    });
    watch(summaryMode, (mode) => {
      categoryPage.value = 1;
      try {
        window.localStorage.setItem(summaryModeStorageKey, mode);
      } catch {
        // A preferência é opcional; o filtro continua funcionando sem persistência.
      }
    });
    watch(categoryLastPage, (lastPage) => {
      if (categoryPage.value > lastPage) categoryPage.value = lastPage;
    });
    watch(recurringLastPage, (lastPage) => {
      if (recurringPage.value > lastPage) recurringPage.value = lastPage;
    });
    const categoryPanelEyebrow = computed(() => ({ cashflow: "Visão do mês", total: "Visão do mês", expenses: "Gastos do mês", fixed: "Recorrentes do mês" }[summaryMode.value]));
    const categoryPanelTitle = computed(() => ({
      cashflow: "Para onde foi seu dinheiro",
      total: "Para onde foi seu dinheiro",
      expenses: "Gastos por categoria",
      fixed: "Recorrentes por categoria",
    }[summaryMode.value]));
    const categoryDetailsRoute = computed(() => summaryMode.value === "fixed" ? "/recurring-expenses" : "/financial-management");
    const categoryEmptyTitle = computed(() => summaryMode.value === "fixed" ? "Nenhum recorrente previsto neste mês" : "Seu mês começa por aqui");
    const categoryEmptyDescription = computed(() => summaryMode.value === "fixed"
      ? "Cadastre compromissos recorrentes para acompanhar os valores recorrentes por categoria."
      : "Quando houver lançamentos, você verá como seus gastos estão distribuídos.");
    const categoryEmptyAction = computed(() => summaryMode.value === "fixed" ? "Adicionar compromisso recorrente" : "Registrar primeiro gasto");
    const categoryEmptyRoute = computed(() => summaryMode.value === "fixed"
      ? "/recurring-expenses"
      : { path: "/financial-management", query: { new: "expense" } });
    const currentMonthFixed = computed(() => recurringAmountForMonth(recurringItems.value, currentMonthStart, currentMonthEnd));
    const previousMonthFixed = computed(() => recurringAmountForMonth(recurringItems.value, previousMonthStart, previousMonthEnd));
    const currentWeekFixed = computed(() => recurringAmountBetween(recurringItems.value, currentWeekStart, currentWeekEnd));
    const last15DaysFixed = computed(() => recurringAmountBetween(recurringItems.value, last15DaysStart, endOfDay(today)));
    const currentMonthRaw = computed(() => selectedAmount(Number(dashboard.value.current_month?.raw || 0), currentMonthFixed.value));
    const previousMonthRaw = computed(() => selectedAmount(Number(dashboard.value.previous_month?.raw || 0), previousMonthFixed.value));
    const currentWeekRaw = computed(() => selectedAmount(Number(dashboard.value.current_week?.raw || 0), currentWeekFixed.value));
    const last15DaysRaw = computed(() => selectedAmount(Number(dashboard.value.last_15_days?.raw || 0), last15DaysFixed.value));
    const currentMonthValue = computed(() => money(currentMonthRaw.value));
    const currentMonthIncomeValue = computed(() => money(currentMonthIncomeRaw.value));
    const currentMonthOutflowValue = computed(() => money(currentMonthRaw.value));
    const currentMonthBalanceRaw = computed(() => currentMonthIncomeRaw.value - currentMonthRaw.value);
    const currentMonthBalanceValue = computed(() => money(currentMonthBalanceRaw.value));
    const currentMonthBalanceClass = computed(() => currentMonthBalanceRaw.value < 0
      ? "is-negative"
      : "is-positive");
    const currentMonthOutflowClass = computed(() => currentMonthRaw.value > currentMonthIncomeRaw.value
      ? "summary-card__flow-value--danger"
      : "summary-card__flow-value--outflow");
    const currentWeekValue = computed(() => money(currentWeekRaw.value));
    const last15DaysValue = computed(() => money(last15DaysRaw.value));
    const comparisonPercentage = computed(() => previousMonthRaw.value > 0
      ? ((currentMonthRaw.value - previousMonthRaw.value) / previousMonthRaw.value) * 100
      : null);
    const comparisonClass = computed(() => {
      if (comparisonPercentage.value === null || comparisonPercentage.value === undefined) return "is-neutral";
      if (comparisonPercentage.value > 0) return "is-increase";
      if (comparisonPercentage.value < 0) return "is-decrease";
      return "is-neutral";
    });
    const comparisonIcon = computed(() => {
      if (comparisonPercentage.value === null || comparisonPercentage.value === undefined) return "ph ph-minus";
      if (comparisonPercentage.value > 0) return "ph ph-trend-up";
      if (comparisonPercentage.value < 0) return "ph ph-trend-down";
      return "ph ph-equals";
    });
    const comparisonText = computed(() => {
      const percentage = comparisonPercentage.value;
      const subject = summaryMode.value === "cashflow" ? " nas saídas" : "";
      if (percentage === null || percentage === undefined) return `Ainda sem comparação${subject} com o mês anterior`;
      if (percentage === 0) return `Mesmo valor${subject} do mês anterior`;

      const direction = percentage > 0 ? "a mais" : "a menos";
      return `${Math.abs(percentage).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}% ${direction}${subject} que no mês anterior`;
    });

    function startOfDay(value: Date) {
      return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }

    function endOfDay(value: Date) {
      return new Date(value.getFullYear(), value.getMonth(), value.getDate(), 23, 59, 59, 999);
    }

    function startOfWeek(value: Date) {
      const day = value.getDay();
      const daysSinceMonday = day === 0 ? 6 : day - 1;
      return new Date(value.getFullYear(), value.getMonth(), value.getDate() - daysSinceMonday);
    }

    function dayNumber(value: Date) {
      return Math.floor(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()) / 86400000);
    }

    function daysInMonth(year: number, month: number) {
      return new Date(year, month + 1, 0).getDate();
    }

    function scheduledOccurrences(item: RecurringExpense, start: Date, end: Date) {
      const anchor = parseDate(item.next_due_date);
      if (!anchor) return 0;

      if (item.frequency === "weekly") {
        const firstIndex = Math.ceil((dayNumber(start) - dayNumber(anchor)) / 7);
        const lastIndex = Math.floor((dayNumber(end) - dayNumber(anchor)) / 7);
        return Math.max(0, lastIndex - firstIndex + 1);
      }

      if (item.frequency === "yearly") {
        let count = 0;
        for (let year = start.getFullYear(); year <= end.getFullYear(); year += 1) {
          const day = Math.min(anchor.getDate(), daysInMonth(year, anchor.getMonth()));
          const occurrence = new Date(year, anchor.getMonth(), day, 12);
          if (occurrence >= start && occurrence <= end) count += 1;
        }
        return count;
      }

      if (!item.frequency || item.frequency === "monthly") {
        let count = 0;
        const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
        const finalMonth = new Date(end.getFullYear(), end.getMonth(), 1);
        while (cursor <= finalMonth) {
          const day = Math.min(anchor.getDate(), daysInMonth(cursor.getFullYear(), cursor.getMonth()));
          const occurrence = new Date(cursor.getFullYear(), cursor.getMonth(), day, 12);
          if (occurrence >= start && occurrence <= end) count += 1;
          cursor.setMonth(cursor.getMonth() + 1);
        }
        return count;
      }

      return anchor >= start && anchor <= end ? 1 : 0;
    }

    function recurringAmountBetween(items: RecurringExpense[], start: Date, end: Date) {
      return items.reduce((total, item) => total + (Number(item.amount) || 0) * scheduledOccurrences(item, start, end), 0);
    }

    function monthlyEstimate(item: RecurringExpense) {
      const amount = Number(item.amount) || 0;
      if (item.frequency === "weekly") return amount * 52 / 12;
      if (item.frequency === "yearly") return amount / 12;
      if (!item.frequency || item.frequency === "monthly") return amount;
      return 0;
    }

    function recurringAmountForItemMonth(item: RecurringExpense, start: Date, end: Date) {
      if (!parseDate(item.next_due_date)) return monthlyEstimate(item);
      return (Number(item.amount) || 0) * scheduledOccurrences(item, start, end);
    }

    function recurringAmountForMonth(items: RecurringExpense[], start: Date, end: Date) {
      return items.reduce((total, item) => total + recurringAmountForItemMonth(item, start, end), 0);
    }

    function selectedAmount(expenses: number, fixed: number) {
      if (summaryMode.value === "expenses" || summaryMode.value === "cashflow") return expenses;
      if (summaryMode.value === "fixed") return fixed;
      return expenses + fixed;
    }

    function money(value: number | string) {
      return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(value) || 0);
    }

    function privateValue(value: string) {
      return valuesVisible.value ? value : "••••••";
    }

    function categoryPercentage(item: CategoryDisplay) {
      const highestValue = Math.max(...categoryItems.value.map((category) => category.displayTotal), 0);
      if (!highestValue) return 0;
      return Math.max(4, Math.round((item.displayTotal / highestValue) * 100));
    }

    function categoryColor(item: CategoryDisplay) {
      const index = Math.max(0, categoryItems.value.findIndex((category) => category.key === item.key));
      return categoryColors[index % categoryColors.length];
    }

    function goToCategoryPage(page: number) {
      if (page < 1 || page > categoryLastPage.value || page === categoryPage.value) return;
      categoryPage.value = page;
    }

    function goToRecurringPage(page: number) {
      if (page < 1 || page > recurringLastPage.value || page === recurringPage.value) return;
      recurringPage.value = page;
    }

    function transactionCountLabel(value?: number) {
      const count = Number(value || 0);
      return `${count} ${count === 1 ? "lançamento" : "lançamentos"}`;
    }

    function fixedCountLabel(value?: number) {
      const count = Number(value || 0);
      return `${count} ${count === 1 ? "compromisso recorrente" : "compromissos recorrentes"}`;
    }

    function categoryItemMeta(item: CategoryDisplay) {
      const parts: string[] = [];
      if (summaryMode.value !== "fixed" && item.transactionCount) parts.push(transactionCountLabel(item.transactionCount));
      if (summaryMode.value !== "expenses" && summaryMode.value !== "cashflow" && item.fixedCount) parts.push(fixedCountLabel(item.fixedCount));
      return parts.join(" • ");
    }

    function categoryFixedBadge(item: CategoryDisplay) {
      if (summaryMode.value === "expenses" || summaryMode.value === "cashflow" || item.fixedAmount <= 0) return "";
      return summaryMode.value === "total" && item.expenseAmount > 0 ? "Inclui recorrentes" : "Recorrente";
    }

    function sameCategory(item: RecurringExpense, category: CategoryDisplay) {
      if (category.categoryId !== null) return Number(item.category?.id) === Number(category.categoryId);
      return (item.category?.name || "Sem categoria") === category.category;
    }

    function transactionBelongsToCategory(transaction: FinanceTransactionDetail, category: CategoryDisplay) {
      if (transaction.type !== "expense") return false;
      const transactionCategoryId = transaction.category_id ?? transaction.category?.id ?? null;
      if (category.categoryId === null) return transactionCategoryId === null;
      return Number(transactionCategoryId) === Number(category.categoryId);
    }

    function sourceLabel(value?: string | null) {
      const labels: Record<string, string> = { api: "Manual", whatsapp: "WhatsApp", voice: "Voz" };
      if (!value) return "Manual";
      return labels[value.toLowerCase()] || value;
    }

    function formatDetailDate(value?: string | null) {
      const date = parseDate(value);
      return date
        ? new Intl.DateTimeFormat("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          }).format(date)
        : "Sem data";
    }

    function frequencyLabel(value?: string) {
      return ({ monthly: "Mensal", weekly: "Semanal", yearly: "Anual" } as Record<string, string>)[value || "monthly"] || "Recorrente";
    }

    async function loadCategoryTransactions() {
      const category = selectedCategory.value;
      if (!category || summaryMode.value === "fixed") {
        categoryDetailLoading.value = false;
        return;
      }

      const requestId = ++categoryDetailRequest;
      categoryDetailLoading.value = true;
      categoryDetailError.value = "";
      categoryDetailTransactions.value = [];

      try {
        const transactions: FinanceTransactionDetail[] = [];
        let page = 1;
        let lastPage = 1;
        do {
          const response = await store.dispatch("fetchFinanceCategoryTransactions", {
            month: today.getMonth() + 1,
            year: today.getFullYear(),
            category_id: category.categoryId || 0,
            type: "expense",
            page,
            per_page: 100,
          }) as { data?: FinanceTransactionDetail[]; last_page?: number };
          if (requestId !== categoryDetailRequest) return;
          transactions.push(...(response.data || []).filter((transaction) => transactionBelongsToCategory(transaction, category)));
          lastPage = Number(response.last_page || 1);
          page += 1;
        } while (page <= lastPage);

        categoryDetailTransactions.value = transactions;
      } catch (error) {
        if (requestId !== categoryDetailRequest) return;
        categoryDetailError.value = "Não foi possível carregar os gastos desta categoria.";
      } finally {
        if (requestId === categoryDetailRequest) categoryDetailLoading.value = false;
      }
    }

    function openCategoryDetails(category: CategoryDisplay) {
      selectedCategory.value = category;
      categoryDetailError.value = "";
      categoryDetailTransactions.value = [];
      void loadCategoryTransactions();
    }

    function closeCategoryDetails() {
      categoryDetailRequest += 1;
      selectedTransaction.value = null;
      selectedCategory.value = null;
      categoryDetailTransactions.value = [];
      categoryDetailError.value = "";
      categoryDetailLoading.value = false;
    }

    function reloadCategoryDetails() {
      void loadCategoryTransactions();
    }

    function openTransactionDetails(item: CategoryDetailItem) {
      if (!item.transaction) return;
      selectedTransaction.value = item.transaction;
      detailEditing.value = false;
      detailFeedback.value = "";
    }

    function closeTransactionDetails() {
      if (detailSaving.value) return;
      selectedTransaction.value = null;
      detailEditing.value = false;
      detailFeedback.value = "";
    }

    function formatCurrencyValue(amount: number) {
      return new Intl.NumberFormat("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    }

    function parseCurrencyInput(value: string): number | null {
      const cleaned = String(value || "").replace(/\s/g, "");
      if (!/\d/.test(cleaned)) return null;

      const commaIndex = cleaned.lastIndexOf(",");
      const dotIndex = cleaned.lastIndexOf(".");
      let normalized = "";

      if (commaIndex >= 0 && dotIndex >= 0) {
        const decimalIndex = Math.max(commaIndex, dotIndex);
        const integer = cleaned.slice(0, decimalIndex).replace(/\D/g, "") || "0";
        const decimal = cleaned.slice(decimalIndex + 1).replace(/\D/g, "").slice(0, 2);
        normalized = `${integer}.${decimal.padEnd(2, "0")}`;
      } else if (commaIndex >= 0) {
        const integer = cleaned.slice(0, commaIndex).replace(/\D/g, "") || "0";
        const decimal = cleaned.slice(commaIndex + 1).replace(/\D/g, "").slice(0, 2);
        normalized = `${integer}.${decimal.padEnd(2, "0")}`;
      } else if (dotIndex >= 0) {
        const decimalPart = cleaned.slice(dotIndex + 1).replace(/\D/g, "");
        const dotCount = (cleaned.match(/\./g) || []).length;
        if (decimalPart.length > 0 && decimalPart.length <= 2) {
          const integer = cleaned.slice(0, dotIndex).replace(/\D/g, "") || "0";
          normalized = `${integer}.${decimalPart.padEnd(2, "0")}`;
        } else if (dotCount >= 1) {
          normalized = cleaned.replace(/\D/g, "");
        }
      } else {
        normalized = cleaned.replace(/\D/g, "");
      }

      const amount = Number(normalized);
      return Number.isFinite(amount) && amount <= 9999999999.99 ? amount : null;
    }

    function startTransactionEdit() {
      const transaction = selectedTransaction.value;
      if (!transaction || transaction.can_edit === false) return;
      detailAmountInput.value = formatCurrencyValue(Number(transaction.amount) || 0);
      detailForm.value.category_id = transaction.type === "expense"
        ? (transaction.category_id ?? transaction.category?.id ?? null)
        : null;
      detailForm.value.description = transaction.description?.trim() || "";
      detailFeedback.value = "";
      detailEditing.value = true;
    }

    function cancelTransactionEdit() {
      if (detailSaving.value) return;
      detailEditing.value = false;
      detailFeedback.value = "";
    }

    function sanitizeDetailAmountInput(event: Event) {
      const input = event.target as HTMLInputElement;
      const digits = input.value
        .replace(/\D/g, "")
        .replace(/^0+(?=\d)/, "")
        .slice(0, 12);
      const formatted = digits ? formatCurrencyValue(Number(digits) / 100) : "";
      detailAmountInput.value = formatted;
      if (input.value !== formatted) input.value = formatted;
    }

    function handleDetailAmountPaste(event: ClipboardEvent) {
      const amount = parseCurrencyInput(event.clipboardData?.getData("text") || "");
      detailAmountInput.value = amount !== null && amount > 0 ? formatCurrencyValue(amount) : "";
    }

    async function saveTransactionEdit() {
      const transaction = selectedTransaction.value;
      if (!transaction) return;
      const amount = parseCurrencyInput(detailAmountInput.value);
      if (amount === null || amount <= 0) {
        detailFeedback.value = "Informe um valor válido maior que zero.";
        return;
      }

      detailSaving.value = true;
      detailFeedback.value = "";
      try {
        const updated = await store.dispatch("updateFinanceTransaction", {
          id: transaction.id,
          data: {
            amount: amount.toFixed(2),
            category_id: transaction.type === "expense" ? (detailForm.value.category_id || null) : null,
            description: detailForm.value.description.trim() || null,
          },
        }) as FinanceTransactionDetail;
        selectedTransaction.value = updated;
        detailEditing.value = false;

        if (selectedCategory.value) await loadCategoryTransactions();
        if (selectedPeriod.value) await loadPeriodTransactions();
        if (transaction.type === "income") await loadCurrentMonthIncome();
      } catch (error) {
        detailFeedback.value = "Não foi possível atualizar o lançamento.";
      } finally {
        detailSaving.value = false;
      }
    }

    function transactionDetailIconStyle(transaction: FinanceTransactionDetail) {
      const fallback = transaction.type === "income" ? "#05785d" : "#c74f4a";
      const color = /^#[0-9a-f]{6}$/i.test(transaction.category?.color || "")
        ? transaction.category?.color as string
        : fallback;
      const red = Number.parseInt(color.slice(1, 3), 16);
      const green = Number.parseInt(color.slice(3, 5), 16);
      const blue = Number.parseInt(color.slice(5, 7), 16);
      return { color, backgroundColor: `rgba(${red}, ${green}, ${blue}, 0.14)` };
    }

    function apiDate(value: Date) {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, "0");
      const day = String(value.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }

    function transactionOccursInPeriod(transaction: FinanceTransactionDetail, period: PeriodSelection) {
      if (transaction.type !== "expense") return false;
      const occurredAt = parseDate(transaction.occurred_at);
      return Boolean(occurredAt && occurredAt >= period.start && occurredAt <= period.end);
    }

    async function loadPeriodTransactions() {
      const period = selectedPeriod.value;
      if (!period || summaryMode.value === "fixed") {
        periodDetailLoading.value = false;
        return;
      }

      const requestId = ++periodDetailRequest;
      periodDetailLoading.value = true;
      periodDetailError.value = "";
      periodDetailTransactions.value = [];

      try {
        const transactions: FinanceTransactionDetail[] = [];
        let page = 1;
        let lastPage = 1;
        do {
          const response = await store.dispatch("fetchFinanceCategoryTransactions", {
            start_date: apiDate(period.start),
            end_date: apiDate(period.end),
            type: "expense",
            page,
            per_page: 100,
          }) as { data?: FinanceTransactionDetail[]; last_page?: number };
          if (requestId !== periodDetailRequest) return;
          transactions.push(...(response.data || []).filter((transaction) => transactionOccursInPeriod(transaction, period)));
          lastPage = Number(response.last_page || 1);
          page += 1;
        } while (page <= lastPage);

        periodDetailTransactions.value = transactions;
      } catch (error) {
        if (requestId !== periodDetailRequest) return;
        periodDetailError.value = "Não foi possível carregar os gastos deste período.";
      } finally {
        if (requestId === periodDetailRequest) periodDetailLoading.value = false;
      }
    }

    function openPeriodDetails(periodKey: PeriodKey) {
      selectedPeriod.value = periodKey === "week"
        ? {
            key: "week",
            title: "Esta semana",
            start: currentWeekStart,
            end: currentWeekEnd,
            total: currentWeekRaw.value,
          }
        : {
            key: "last15",
            title: "Últimos 15 dias",
            start: last15DaysStart,
            end: endOfDay(today),
            total: last15DaysRaw.value,
          };
      periodDetailError.value = "";
      periodDetailTransactions.value = [];
      void loadPeriodTransactions();
    }

    function closePeriodDetails() {
      periodDetailRequest += 1;
      selectedTransaction.value = null;
      selectedPeriod.value = null;
      periodDetailTransactions.value = [];
      periodDetailError.value = "";
      periodDetailLoading.value = false;
    }

    function reloadPeriodDetails() {
      void loadPeriodTransactions();
    }

    function parseDate(value?: string | null) {
      if (!value) return null;
      const normalized = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00` : value;
      const date = new Date(normalized);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    function dueDay(value?: string | null) {
      const date = parseDate(value);
      return date ? new Intl.DateTimeFormat("pt-BR", { day: "2-digit" }).format(date) : "--";
    }

    function dueMonth(value?: string | null) {
      const date = parseDate(value);
      return date ? new Intl.DateTimeFormat("pt-BR", { month: "short" }).format(date).replace(".", "") : "sem data";
    }

    async function loadCurrentMonthIncome() {
      const requestId = ++incomeRequest;
      let total = 0;
      let page = 1;
      let lastPage = 1;

      try {
        do {
          const response = await store.dispatch("fetchFinanceCategoryTransactions", {
            month: today.getMonth() + 1,
            year: today.getFullYear(),
            type: "income",
            page,
            per_page: 100,
          }) as { data?: FinanceTransactionDetail[]; last_page?: number };
          if (requestId !== incomeRequest) return;
          total += (response.data || []).reduce((sum, transaction) => sum + (Number(transaction.amount) || 0), 0);
          lastPage = Number(response.last_page || 1);
          page += 1;
        } while (page <= lastPage);

        currentMonthIncomeRaw.value = total;
      } catch (error) {
        if (requestId === incomeRequest) currentMonthIncomeRaw.value = 0;
      } finally {
        if (requestId === incomeRequest) incomeLoaded.value = true;
      }
    }

    function reload() {
      return Promise.all([
        store.dispatch("fetchFinanceDashboard"),
        loadCurrentMonthIncome(),
      ]);
    }

    onMounted(() => {
      void Promise.all([
        reload(),
        store.dispatch("fetchFinanceCategories"),
      ]);
    });

    return {
      categoryColors,
      categoryColor,
      categoryDetailsRoute,
      categoryDetailError,
      categoryDetailEyebrow,
      categoryDetailItems,
      categoryDetailLoading,
      categoryEmptyAction,
      categoryEmptyDescription,
      categoryEmptyRoute,
      categoryEmptyTitle,
      categoryFixedBadge,
      categoryItemMeta,
      categoryPanelEyebrow,
      categoryPanelTitle,
      categoryPage,
      categoryLastPage,
      categoryPaginationPages,
      categoryPaginationSummary,
      categoryPercentage,
      categoryPreview,
      cancelTransactionEdit,
      closeCategoryDetails,
      closePeriodDetails,
      closeTransactionDetails,
      comparisonClass,
      comparisonIcon,
      comparisonText,
      currentMonthBalanceClass,
      currentMonthBalanceValue,
      currentMonthLabel,
      currentMonthIncomeValue,
      currentMonthOutflowClass,
      currentMonthOutflowValue,
      currentMonthValue,
      currentWeekValue,
      detailAmountInput,
      detailEditing,
      detailFeedback,
      detailForm,
      detailSaving,
      dueDay,
      dueMonth,
      error,
      financeCategories,
      hasDashboardData,
      goToCategoryPage,
      goToRecurringPage,
      last15DaysValue,
      last15DaysTitle,
      loading,
      monthSummaryTitle,
      money,
      handleDetailAmountPaste,
      openTransactionDetails,
      openPeriodDetails,
      periodDetailError,
      periodDetailEyebrow,
      periodDetailItems,
      periodDetailLoading,
      privateValue,
      recurringPreview,
      recurringPage,
      recurringLastPage,
      recurringPaginationPages,
      recurringPaginationSummary,
      recurringTotal,
      reload,
      reloadCategoryDetails,
      reloadPeriodDetails,
      openCategoryDetails,
      selectedCategory,
      selectedPeriod,
      selectedTransaction,
      sanitizeDetailAmountInput,
      saveTransactionEdit,
      startTransactionEdit,
      summaryInitializing,
      summaryMode,
      transactionCountLabel,
      currentWeekTitle,
      formatDetailDate,
      sourceLabel,
      transactionDetailIconStyle,
      valuesVisible,
    };
  },
});
</script>

<style scoped>
.finance-dashboard {
  --dashboard-accent: #06a77d;
  --dashboard-accent-dark: #05785d;
  --dashboard-ink: #171629;
  --dashboard-muted: #747487;
  --dashboard-surface: #ffffff;
  --dashboard-soft: #f5f5f8;
  --dashboard-border: #e9e9f0;
  color: var(--dashboard-ink);
  margin: 0 auto;
  max-width: 1480px;
  width: 100%;
}

.dashboard-intro {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}

.panel-heading__eyebrow {
  color: var(--dashboard-accent-dark);
  display: block;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  margin-bottom: 5px;
  text-transform: uppercase;
}

.dashboard-intro__whatsapp {
  align-items: center;
  background: #e9f7f3;
  border: 1px solid rgba(6, 167, 125, 0.3);
  border-radius: 50%;
  color: var(--dashboard-accent-dark);
  display: flex;
  flex: 0 0 44px;
  font-size: 26px;
  height: 44px;
  justify-content: center;
  text-decoration: none;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.dashboard-intro__whatsapp:hover {
  background: var(--dashboard-accent-dark);
  color: #ffffff;
}

.dashboard-intro__whatsapp:focus-visible {
  outline: 2px solid var(--dashboard-accent-dark);
  outline-offset: 3px;
}

.dashboard-intro__actions {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
}

.refresh-button {
  align-items: center;
  background: var(--dashboard-surface);
  border: 1px solid var(--dashboard-border);
  border-radius: 50%;
  color: var(--dashboard-ink);
  display: flex;
  flex: 0 0 44px;
  font-size: 20px;
  height: 44px;
  justify-content: center;
  transition: border-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.refresh-button:hover:not(:disabled) {
  border-color: var(--dashboard-accent);
  color: var(--dashboard-accent-dark);
  transform: translateY(-1px);
}

.refresh-button:disabled {
  cursor: wait;
  opacity: 0.65;
}

.privacy-button.is-hidden {
  background: #e9f7f3;
  border-color: rgba(6, 167, 125, 0.3);
  color: var(--dashboard-accent-dark);
}

.is-spinning {
  animation: dashboard-spin 0.9s linear infinite;
}

.dashboard-feedback {
  align-items: center;
  border-radius: 16px;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 18px;
  padding: 14px 16px;
}

.dashboard-feedback--error {
  background: #fff2f2;
  border: 1px solid #ffd6d6;
  color: #8d2929;
}

.dashboard-feedback > div {
  align-items: center;
  display: flex;
  gap: 12px;
}

.dashboard-feedback i {
  font-size: 24px;
}

.dashboard-feedback strong,
.dashboard-feedback span {
  display: block;
}

.dashboard-feedback span {
  font-size: 13px;
  margin-top: 2px;
}

.dashboard-feedback button {
  background: transparent;
  border: 0;
  color: inherit;
  font-weight: 800;
  min-height: 44px;
  padding: 0 8px;
  white-space: nowrap;
}

.summary-card {
  background:
    radial-gradient(circle at 92% 4%, rgba(255, 255, 255, 0.16), transparent 32%),
    linear-gradient(140deg, #075b49 0%, #073d36 54%, #142b2a 100%);
  border-radius: 24px;
  box-shadow: 0 18px 46px rgba(5, 91, 73, 0.22);
  color: #ffffff;
  min-height: 218px;
  overflow: hidden;
  padding: 28px;
  position: relative;
}

.summary-card::after {
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 50%;
  content: "";
  height: 170px;
  position: absolute;
  right: -72px;
  top: 44px;
  width: 170px;
}

.summary-card__topline {
  align-items: flex-start;
  color: rgba(255, 255, 255, 0.76);
  display: flex;
  font-size: 14px;
  font-weight: 700;
  justify-content: space-between;
  position: relative;
  z-index: 1;
}

.summary-card__value {
  display: block;
  font-size: clamp(38px, 6vw, 54px);
  font-weight: 800;
  letter-spacing: -0.045em;
  line-height: 1;
  margin: 18px 0 22px;
  overflow-wrap: anywhere;
  position: relative;
  z-index: 1;
}

.summary-card__comparison {
  align-items: center;
  background: rgba(255, 255, 255, 0.11);
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 999px;
  display: flex;
  font-size: 13px;
  font-weight: 700;
  gap: 7px;
  margin-left: auto;
  margin-right: auto;
  max-width: 100%;
  padding: 8px 12px;
  position: relative;
  width: fit-content;
  z-index: 1;
}

.summary-card__flow {
  display: grid;
  gap: 24px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin: 18px 0 22px;
  position: relative;
  z-index: 1;
}

.summary-card__flow small {
  color: rgba(255, 255, 255, 0.66);
  display: block;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  margin-bottom: 5px;
  text-transform: uppercase;
}

.summary-card__flow-outflow {
  justify-self: end;
  width: fit-content;
}

.summary-card__flow-outflow .summary-card__flow-value {
  text-align: right;
}

.summary-card__flow-value {
  display: block;
  font-size: clamp(24px, 4vw, 42px);
  font-weight: 800;
  letter-spacing: -0.045em;
  line-height: 1.05;
  overflow-wrap: anywhere;
}

.summary-card__flow-value--income {
  color: #63f5b3;
}

.summary-card__flow-value--outflow {
  color: #ffffff;
}

.summary-card__flow-value--danger {
  color: #ff8585;
}

.summary-card__balance {
  align-items: center;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 13px;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin: -8px 0 12px;
  padding: 10px 12px;
  position: relative;
  z-index: 1;
}

.summary-card__balance small,
.summary-card__balance span {
  display: block;
}

.summary-card__balance small {
  color: #ffffff;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.summary-card__balance span {
  color: rgba(255, 255, 255, 0.62);
  font-size: 10px;
  margin-top: 1px;
}

.summary-card__balance strong {
  flex: 0 0 auto;
  font-size: clamp(20px, 3vw, 27px);
  font-weight: 800;
  letter-spacing: -0.035em;
}

.summary-card__balance strong.is-positive {
  color: #63f5b3;
}

.summary-card__balance strong.is-negative {
  color: #ff8585;
}

.summary-card__balance strong.is-hidden {
  color: #ffffff;
  letter-spacing: 0.08em;
}

.summary-card__filter {
  display: block;
  flex: 0 0 auto;
  margin-left: auto;
}

.summary-card__filter > span {
  color: rgba(255, 255, 255, 0.66);
  display: block;
  font-size: 8px;
  font-weight: 800;
  letter-spacing: 0.08em;
  margin: 0 0 4px 2px;
  text-transform: uppercase;
}

.summary-card__filter select {
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 10px;
  color: #ffffff;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 800;
  min-height: 36px;
  min-width: 94px;
  padding: 0 9px;
}

.summary-card__filter select:focus-visible {
  outline: 3px solid rgba(94, 240, 190, 0.3);
  outline-offset: 2px;
}

.summary-card__filter option {
  background: #ffffff;
  color: #171629;
}

.summary-card__comparison.is-increase {
  color: #ffd2ca;
}

.summary-card__comparison.is-decrease {
  color: #8ef5ce;
}

.summary-card--loading {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.skeleton {
  animation: skeleton-pulse 1.4s ease-in-out infinite;
  background: rgba(255, 255, 255, 0.17);
  border-radius: 999px;
  display: block;
}

.skeleton--label {
  height: 15px;
  width: 130px;
}

.skeleton--value {
  height: 45px;
  margin-top: 12px;
  width: min(290px, 80%);
}

.skeleton--chip {
  height: 32px;
  width: min(245px, 75%);
}

.quick-actions {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin: 18px 0;
}

.quick-action {
  align-items: center;
  background: var(--dashboard-surface);
  border: 1px solid var(--dashboard-border);
  border-radius: 18px;
  color: var(--dashboard-ink);
  display: flex;
  font-size: 14px;
  font-weight: 800;
  gap: 11px;
  min-height: 66px;
  padding: 11px 14px;
  text-decoration: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.quick-action:hover {
  border-color: rgba(6, 167, 125, 0.5);
  box-shadow: 0 10px 24px rgba(23, 22, 41, 0.08);
  color: var(--dashboard-ink);
  transform: translateY(-2px);
}

.quick-action__icon {
  align-items: center;
  background: #e7f8f2;
  border-radius: 14px;
  box-sizing: border-box;
  color: var(--dashboard-accent-dark);
  display: inline-flex;
  flex: 0 0 42px;
  font-size: 20px;
  height: 42px;
  justify-content: center;
  min-width: 42px;
  width: 42px;
}

.quick-action--primary {
  background: #effbf7;
  border-color: #c9eee2;
}

.quick-action--primary .quick-action__icon {
  background: var(--dashboard-accent);
  color: #ffffff;
}

.quick-actions--loading {
  height: 66px;
}

.skeleton--action {
  background: #e5e5eb;
  border-radius: 18px;
  height: 66px;
}

.period-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-bottom: 18px;
}

.period-card {
  align-items: center;
  background: var(--dashboard-surface);
  border: 1px solid var(--dashboard-border);
  border-radius: 18px;
  cursor: pointer;
  display: flex;
  gap: 14px;
  min-width: 0;
  outline: 0;
  padding: 17px;
  transition: background-color 0.2s ease, transform 0.2s ease;
}

.period-card:focus-visible {
  background: var(--dashboard-soft);
  box-shadow: inset 0 0 0 2px rgba(6, 180, 138, 0.3);
}

@media (hover: hover) {
  .period-card:hover {
    background: var(--dashboard-soft);
    transform: translateY(-2px);
  }
}

.period-card__icon {
  align-items: center;
  border-radius: 14px;
  display: inline-flex;
  flex: 0 0 44px;
  font-size: 21px;
  height: 44px;
  justify-content: center;
}

.period-card__icon i {
  display: block;
  line-height: 1;
}

.period-card__icon--week {
  background: #eef0ff;
  color: #5661d9;
}

.period-card__icon--days {
  background: #fff4df;
  color: #b77408;
}

.period-card div {
  min-width: 0;
}

.period-card > div > span,
.recurring-summary span {
  color: var(--dashboard-muted);
  display: block;
  font-size: 12px;
  font-weight: 700;
}

.period-card strong {
  color: var(--dashboard-ink);
  display: block;
  font-size: clamp(18px, 2.6vw, 23px);
  font-weight: 800;
  line-height: 1.15;
  margin-top: 5px;
  overflow-wrap: anywhere;
}

.dashboard-columns {
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(0, 1.35fr) minmax(320px, 0.85fr);
}

.category-panel {
  grid-column: 1 / -1;
}

.dashboard-columns > .period-grid {
  margin-bottom: 0;
}

@media (min-width: 992px) {
  .dashboard-columns > .period-grid {
    align-items: stretch;
  }

  .dashboard-columns > .period-grid .period-card {
    align-items: flex-start;
    border-radius: 24px;
    flex-direction: column;
    gap: 18px;
    justify-content: center;
    min-height: 210px;
    padding: clamp(24px, 2.2vw, 34px);
  }

  .dashboard-columns > .period-grid .period-card__icon {
    border-radius: 18px;
    flex-basis: 58px;
    font-size: 28px;
    height: 58px;
    width: 58px;
  }

  .dashboard-columns > .period-grid .period-card > div > span {
    font-size: 14px;
    letter-spacing: -0.01em;
  }

  .dashboard-columns > .period-grid .period-card strong {
    font-size: clamp(30px, 2.5vw, 42px);
    letter-spacing: -0.055em;
    line-height: 1;
    margin-top: 8px;
  }
}

.dashboard-panel {
  background: var(--dashboard-surface);
  border: 1px solid var(--dashboard-border);
  border-radius: 22px;
  min-width: 0;
  padding: 22px;
}

.panel-heading {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 22px;
}

.panel-heading h2 {
  color: var(--dashboard-ink);
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.025em;
  margin: 0;
}

.panel-heading > a,
.recurring-empty a {
  color: var(--dashboard-accent-dark);
  font-size: 13px;
  font-weight: 800;
  min-height: 44px;
  padding-top: 5px;
  text-decoration: none;
  white-space: nowrap;
}

.panel-heading > .panel-heading__action {
  align-items: center;
  background: #e8f8f3;
  border: 1px solid #c9eee2;
  border-radius: 999px;
  color: var(--dashboard-accent-dark);
  display: inline-flex;
  font-size: 11px;
  gap: 2px;
  min-height: 26px;
  padding: 0 5px;
  transition: background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.panel-heading > .panel-heading__action:hover {
  background: #d9f3eb;
  border-color: #aee4d4;
  color: var(--dashboard-accent-dark);
  transform: translateX(1px);
}

.panel-heading__action i {
  font-size: 11px;
  line-height: 1;
}

.category-list {
  display: grid;
  gap: 18px;
}

.category-item {
  background: transparent;
  border-radius: 12px;
  cursor: pointer;
  margin: -8px -10px;
  outline: 0;
  padding: 8px 10px;
  transition: background-color 0.2s ease, transform 0.2s ease;
}

.category-item:focus-visible {
  background: rgba(6, 180, 138, 0.09);
  box-shadow: inset 0 0 0 2px rgba(6, 180, 138, 0.3);
}

@media (hover: hover) {
  .category-item:hover {
    background: rgba(6, 180, 138, 0.09);
    transform: translateX(3px);
  }
}

.category-item:active {
  transform: scale(0.995);
}

.category-item__content {
  align-items: center;
  display: grid;
  gap: 11px;
  grid-template-columns: 10px minmax(0, 1fr) auto;
}

.category-item__marker {
  border-radius: 50%;
  height: 9px;
  width: 9px;
}

.category-item__name {
  min-width: 0;
}

.category-item__title {
  align-items: center;
  display: flex;
  gap: 7px;
  min-width: 0;
}

.category-item__title > strong {
  color: var(--dashboard-ink);
  font-size: 14px;
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.category-item__meta {
  color: var(--dashboard-muted);
  display: block;
  font-size: 11px;
  margin-top: 2px;
}

.category-item__fixed-badge {
  background: #e7f8f2;
  border: 1px solid #c9eee2;
  border-radius: 999px;
  color: var(--dashboard-accent-dark);
  flex: 0 0 auto;
  font-size: 8px;
  font-weight: 800;
  letter-spacing: 0.03em;
  padding: 3px 6px;
  text-transform: uppercase;
}

.category-item__value {
  color: var(--dashboard-ink);
  font-size: 14px;
  font-weight: 800;
  white-space: nowrap;
}

.category-item__track {
  background: #eeeeF3;
  border-radius: 999px;
  height: 6px;
  margin: 9px 0 0 21px;
  overflow: hidden;
}

.category-item__track span {
  border-radius: inherit;
  display: block;
  height: 100%;
  transition: width 0.45s ease;
}

.category-pagination {
  align-items: center;
  border-top: 1px solid var(--dashboard-border);
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  padding-top: 16px;
}

.category-pagination__summary {
  color: var(--dashboard-muted);
  font-size: 12px;
}

.category-pagination__actions {
  display: flex;
  gap: 6px;
}

.category-pagination__actions button,
.category-pagination__mobile button {
  background: var(--dashboard-surface);
  border: 1px solid var(--dashboard-border);
  border-radius: 9px;
  color: var(--dashboard-muted);
  font-size: 11px;
  font-weight: 800;
  min-height: 34px;
  min-width: 34px;
  padding: 0 9px;
}

.category-pagination__actions button.active {
  background: var(--dashboard-accent-dark);
  border-color: var(--dashboard-accent-dark);
  color: #fff;
}

.category-pagination__actions button:disabled,
.category-pagination__mobile button:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.category-pagination__mobile {
  display: none;
}

.category-detail {
  align-items: center;
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 20px;
  position: fixed;
  z-index: 1060;
}

.category-detail__backdrop {
  background: rgba(17, 24, 39, 0.54);
  inset: 0;
  position: absolute;
}

.category-detail__content {
  background: var(--dashboard-surface);
  border-radius: 22px;
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.24);
  max-height: calc(100vh - 40px);
  max-width: 560px;
  overflow-y: auto;
  padding: 24px;
  position: relative;
  width: min(100%, 560px);
  z-index: 1;
}

.category-detail__sticky-header,
.transaction-detail__sticky-header {
  background: var(--dashboard-surface);
  margin: -24px -4px 0;
  padding: 24px 4px 0;
  position: sticky;
  top: -24px;
  z-index: 3;
}

.category-detail__handle {
  display: none;
}

.category-detail__header {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  margin-bottom: 18px;
}

.category-detail__header h2 {
  color: var(--dashboard-ink);
  font-size: 23px;
  font-weight: 800;
  letter-spacing: -0.03em;
  margin: 0;
}

.category-detail__header button {
  align-items: center;
  background: var(--dashboard-soft);
  border: 0;
  border-radius: 50%;
  color: var(--dashboard-ink);
  display: flex;
  flex: 0 0 38px;
  font-size: 18px;
  height: 38px;
  justify-content: center;
  width: 38px;
}

.category-detail__summary {
  align-items: center;
  background: var(--dashboard-soft);
  border-radius: 16px;
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
  padding: 14px;
}

.category-detail__summary > span {
  border-radius: 50%;
  flex: 0 0 12px;
  height: 12px;
  width: 12px;
}

.category-detail__summary > .period-detail__marker {
  background: #5f6df2;
}

.category-detail__summary small,
.category-detail__summary strong {
  display: block;
}

.category-detail__summary small {
  color: var(--dashboard-muted);
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
}

.category-detail__summary strong {
  color: var(--dashboard-ink);
  font-size: 24px;
  letter-spacing: -0.03em;
  margin-top: 2px;
}

.category-detail__projection {
  color: var(--dashboard-muted);
  font-size: 11px;
  font-weight: 500;
  line-height: 1.35;
  margin: 5px 0 0;
}

.category-detail__projection b {
  color: var(--dashboard-muted);
  font-weight: 700;
}

.category-detail__list {
  display: grid;
  margin-top: 8px;
}

.category-detail__item {
  align-items: center;
  border-bottom: 1px solid var(--dashboard-border);
  display: grid;
  gap: 11px;
  grid-template-columns: 42px minmax(0, 1fr) auto;
  padding: 12px 0;
}

.category-detail__item:last-child {
  border-bottom: 0;
}

.category-detail__item.is-clickable {
  border-radius: 12px;
  cursor: pointer;
  outline: 0;
  padding-left: 6px;
  padding-right: 6px;
  transition: background 0.18s ease, transform 0.18s ease;
}

.category-detail__item.is-clickable:hover,
.category-detail__item.is-clickable:focus-visible {
  background: var(--dashboard-soft);
  transform: translateX(2px);
}

.category-detail__icon {
  align-items: center;
  background: rgba(239, 106, 103, 0.13);
  border-radius: 12px;
  color: #c74f4a;
  display: flex;
  font-size: 18px;
  height: 42px;
  justify-content: center;
  width: 42px;
}

.category-detail__icon.is-fixed {
  background: rgba(6, 180, 138, 0.13);
  color: var(--dashboard-accent-dark);
}

.category-detail__item > div {
  min-width: 0;
}

.category-detail__item > div strong,
.category-detail__item > div span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.category-detail__item > div strong {
  color: var(--dashboard-ink);
  font-size: 13px;
  font-weight: 800;
}

.category-detail__item > div span {
  color: var(--dashboard-muted);
  font-size: 10px;
  margin-top: 4px;
}

.category-detail__item > strong {
  color: var(--dashboard-ink);
  font-size: 13px;
  font-weight: 800;
  white-space: nowrap;
}

.category-detail__loading,
.category-detail__error,
.category-detail__empty {
  align-items: center;
  color: var(--dashboard-muted);
  display: flex;
  justify-content: center;
  min-height: 150px;
  text-align: center;
}

.category-detail__loading {
  flex-direction: column;
  font-size: 12px;
  gap: 8px;
}

.category-detail__loading i {
  color: var(--dashboard-accent-dark);
  font-size: 26px;
}

.category-detail__error {
  flex-direction: column;
  font-size: 12px;
  gap: 10px;
}

.category-detail__error button {
  background: var(--dashboard-accent-dark);
  border: 0;
  border-radius: 10px;
  color: #fff;
  font-weight: 800;
  min-height: 38px;
  padding: 0 14px;
}

.category-detail__empty {
  flex-direction: column;
  gap: 5px;
}

.category-detail__empty i {
  color: var(--dashboard-accent-dark);
  font-size: 28px;
  margin-bottom: 5px;
}

.category-detail__empty strong {
  color: var(--dashboard-ink);
  font-size: 14px;
}

.category-detail__empty span {
  font-size: 11px;
}

.transaction-detail {
  align-items: center;
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 20px;
  position: fixed;
  z-index: 1070;
}

.transaction-detail__backdrop {
  background: rgba(17, 24, 39, 0.62);
  inset: 0;
  position: absolute;
}

.transaction-detail__content {
  background: var(--dashboard-surface);
  border-radius: 22px;
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.3);
  max-height: calc(100vh - 40px);
  max-width: 540px;
  overflow-y: auto;
  padding: 24px;
  position: relative;
  width: min(100%, 540px);
  z-index: 1;
}

.transaction-detail__handle { display: none; }

.transaction-detail__header {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  margin-bottom: 18px;
}

.transaction-detail__header h2 {
  color: var(--dashboard-ink);
  font-size: 23px;
  font-weight: 800;
  letter-spacing: -0.03em;
  margin: 0;
}

.transaction-detail__header button {
  align-items: center;
  background: var(--dashboard-soft);
  border: 0;
  border-radius: 50%;
  color: var(--dashboard-ink);
  display: flex;
  flex: 0 0 38px;
  font-size: 18px;
  height: 38px;
  justify-content: center;
  width: 38px;
}

.transaction-detail__header-actions { align-items: center; display: flex; gap: 8px; }
.transaction-detail__header .transaction-detail__edit-button {
  border-radius: 12px;
  gap: 6px;
  padding: 0 13px;
  width: auto;
}
.transaction-detail__edit-button span { font-size: 12px; font-weight: 800; }

.transaction-detail__amount {
  align-items: center;
  background: var(--dashboard-soft);
  border: 1px solid var(--dashboard-border);
  border-radius: 17px;
  display: flex;
  gap: 13px;
  margin-bottom: 17px;
  padding: 15px;
}

.transaction-detail__amount > span {
  align-items: center;
  border-radius: 13px;
  display: flex;
  flex: 0 0 46px;
  font-size: 22px;
  height: 46px;
  justify-content: center;
  width: 46px;
}

.transaction-detail__amount small,
.transaction-detail__amount strong { display: block; }
.transaction-detail__amount small { color: var(--dashboard-muted); font-size: 10px; font-weight: 800; text-transform: uppercase; }
.transaction-detail__amount strong { color: #a94541; font-size: 25px; letter-spacing: -0.03em; margin-top: 2px; }
.transaction-detail__amount.is-income strong { color: var(--dashboard-accent-dark); }

.transaction-detail__grid {
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr 1fr;
  margin: 0 0 17px;
}

.transaction-detail__grid > div {
  border: 1px solid var(--dashboard-border);
  border-radius: 13px;
  min-width: 0;
  padding: 12px;
}

.transaction-detail__grid dt { color: var(--dashboard-muted); font-size: 9px; font-weight: 800; margin-bottom: 4px; text-transform: uppercase; }
.transaction-detail__grid dd { color: var(--dashboard-ink); font-size: 13px; font-weight: 800; margin: 0; overflow-wrap: anywhere; }

.transaction-detail__amount-field,
.transaction-detail__form-field { display: block; margin-bottom: 15px; }
.transaction-detail__amount-field > span,
.transaction-detail__form-field > span {
  color: var(--dashboard-muted);
  display: block;
  font-size: 10px;
  font-weight: 800;
  margin-bottom: 6px;
  text-transform: uppercase;
}
.transaction-detail__amount-field > div {
  align-items: center;
  border-bottom: 2px solid var(--dashboard-border);
  display: flex;
  gap: 8px;
  padding: 2px 0 8px;
  transition: border-color 0.2s ease;
}
.transaction-detail__amount-field > div:focus-within { border-color: var(--dashboard-accent); }
.transaction-detail__amount-field > div > span { color: var(--dashboard-muted); font-size: 21px; font-weight: 800; }
.transaction-detail__amount-field input {
  background: transparent;
  border: 0;
  color: var(--dashboard-ink);
  font-size: 27px;
  font-weight: 800;
  letter-spacing: -0.04em;
  min-width: 0;
  outline: 0;
  width: 100%;
}
.transaction-detail__amount-field input::placeholder { color: #c3c2cb; }
.transaction-detail__edit-form textarea { line-height: 1.45; min-height: 82px; resize: vertical; }
.transaction-detail__grid--metadata { margin-top: 2px; }
.transaction-detail__grid--metadata > div {
  background: var(--dashboard-soft);
  border-color: transparent;
  cursor: not-allowed;
  opacity: 0.52;
}
.transaction-detail__grid--metadata dt,
.transaction-detail__grid--metadata dd { color: var(--dashboard-muted); }
.transaction-detail__grid--metadata dd { font-weight: 700; }
.transaction-detail__edit-actions { display: grid; gap: 9px; grid-template-columns: 1fr 1.35fr; margin-bottom: 12px; }
.transaction-detail__edit-actions button {
  align-items: center;
  background: var(--dashboard-soft);
  border: 0;
  border-radius: 12px;
  color: var(--dashboard-ink);
  display: flex;
  font-size: 13px;
  font-weight: 800;
  gap: 7px;
  justify-content: center;
  min-height: 46px;
  padding: 0 14px;
}
.transaction-detail__edit-actions button.is-primary { background: var(--dashboard-accent-dark); color: #fff; }
.transaction-detail__edit-actions button:disabled { opacity: 0.65; }
.transaction-detail__feedback {
  background: #fff1f0;
  border: 1px solid #ffd5d1;
  border-radius: 11px;
  color: #9c3732;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 15px;
  padding: 10px 12px;
}

.transaction-detail__message {
  background: #effaf6;
  border: 1px solid #c9eee2;
  border-radius: 17px;
  padding: 15px;
}

.transaction-detail__message > header { align-items: center; display: flex; gap: 10px; }
.transaction-detail__message > header > span { align-items: center; background: var(--dashboard-accent-dark); border-radius: 11px; color: #fff; display: flex; flex: 0 0 36px; font-size: 18px; height: 36px; justify-content: center; width: 36px; }
.transaction-detail__message > header strong,
.transaction-detail__message > header small { display: block; }
.transaction-detail__message > header strong { color: var(--dashboard-ink); font-size: 13px; font-weight: 800; }
.transaction-detail__message > header small { color: var(--dashboard-muted); font-size: 10px; margin-top: 2px; }
.transaction-detail__message blockquote,
.transaction-detail__message p { color: var(--dashboard-ink); font-size: 14px; line-height: 1.55; margin: 13px 0 0; overflow-wrap: anywhere; white-space: pre-wrap; }
.transaction-detail__message.is-empty { background: var(--dashboard-soft); border-color: var(--dashboard-border); }
.transaction-detail__message.is-empty > header > span { background: #dedde5; color: var(--dashboard-muted); }
.transaction-detail__message.is-empty p { color: var(--dashboard-muted); font-size: 12px; }
.transaction-detail__recurring { align-items: center; color: var(--dashboard-muted); display: flex; font-size: 11px; font-weight: 800; gap: 6px; margin-top: 13px; }

.empty-state {
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 24px 16px 20px;
  text-align: center;
}

.empty-state__icon {
  align-items: center;
  background: #e8f8f3;
  border-radius: 50%;
  color: var(--dashboard-accent-dark);
  display: inline-flex;
  font-size: 28px;
  height: 60px;
  justify-content: center;
  margin-bottom: 14px;
  width: 60px;
}

.empty-state strong {
  color: var(--dashboard-ink);
  font-size: 16px;
}

.empty-state p {
  color: var(--dashboard-muted);
  line-height: 1.5;
  margin: 6px 0 14px;
  max-width: 360px;
}

.empty-state a {
  color: var(--dashboard-accent-dark);
  font-weight: 800;
}

.recurring-summary {
  background: #f4f5f7;
  border-radius: 16px;
  margin-bottom: 18px;
  padding: 16px;
}

.recurring-summary strong {
  color: var(--dashboard-ink);
  display: block;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.035em;
  margin-top: 4px;
  overflow-wrap: anywhere;
}

.recurring-list {
  display: grid;
  gap: 4px;
}

.recurring-item {
  align-items: center;
  border-bottom: 1px solid var(--dashboard-border);
  display: grid;
  gap: 11px;
  grid-template-columns: 42px minmax(0, 1fr) auto;
  padding: 10px 0;
}

.recurring-item:last-child {
  border-bottom: 0;
}

.recurring-item__date {
  align-items: center;
  background: #eef8f5;
  border-radius: 11px;
  color: var(--dashboard-accent-dark);
  display: flex;
  flex-direction: column;
  height: 42px;
  justify-content: center;
  line-height: 1;
  width: 42px;
}

.recurring-item__date strong {
  font-size: 14px;
}

.recurring-item__date small {
  font-size: 9px;
  font-weight: 800;
  margin-top: 3px;
  max-width: 38px;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.recurring-item__name {
  min-width: 0;
}

.recurring-item__name strong,
.recurring-item__name span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.recurring-item__name strong {
  color: var(--dashboard-ink);
  font-size: 13px;
  font-weight: 800;
}

.recurring-item__name span {
  color: var(--dashboard-muted);
  font-size: 11px;
  margin-top: 2px;
}

.recurring-item__value {
  color: var(--dashboard-ink);
  font-size: 13px;
  font-weight: 800;
  white-space: nowrap;
}

.recurring-empty {
  align-items: center;
  color: var(--dashboard-muted);
  display: flex;
  font-size: 13px;
  justify-content: space-between;
}

@media (max-width: 991px) {
  .dashboard-columns {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .finance-dashboard {
    padding-bottom: 8px;
  }

  .dashboard-intro {
    margin-bottom: 16px;
  }

  .summary-card {
    border-radius: 22px;
    min-height: 218px;
    padding: 22px 20px;
  }

  .summary-card--cashflow {
    min-height: 278px;
  }

  .summary-card__value {
    font-size: clamp(36px, 11vw, 46px);
    margin: 15px 0 20px;
  }

  .summary-card__flow {
    gap: 14px;
    margin: 15px 0 20px;
  }

  .summary-card__flow-value {
    font-size: clamp(23px, 6.8vw, 34px);
  }

  .summary-card__balance strong {
    font-size: 21px;
  }

  .summary-card__comparison {
    font-size: 11px;
    padding-left: 10px;
    padding-right: 10px;
  }

  .summary-card__filter select {
    min-width: 88px;
  }

  .quick-actions {
    gap: 8px;
    margin: 14px 0;
  }

  .quick-action {
    align-items: center;
    border-radius: 16px;
    flex-direction: column;
    font-size: 11px;
    gap: 7px;
    justify-content: center;
    min-height: 88px;
    overflow: hidden;
    padding: 10px 4px;
    text-align: center;
  }

  .quick-action__icon {
    border-radius: 13px;
    flex: 0 0 40px;
    height: 40px;
    min-width: 40px;
    width: 40px;
  }

  .quick-action > span:last-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
  }

  .period-grid {
    gap: 10px;
    margin-bottom: 14px;
  }

  .period-card {
    align-items: flex-start;
    border-radius: 16px;
    flex-direction: column;
    gap: 10px;
    padding: 14px;
  }

  .period-card__icon {
    flex-basis: 38px;
    height: 38px;
    width: 38px;
  }

  .period-card strong {
    font-size: clamp(17px, 5vw, 21px);
  }

  .dashboard-columns {
    gap: 14px;
  }

  .dashboard-panel {
    border-radius: 20px;
    padding: 18px 16px;
  }

  .panel-heading {
    margin-bottom: 18px;
  }

  .panel-heading h2 {
    font-size: 18px;
  }

  .category-item__value,
  .recurring-item__value {
    font-size: 12px;
  }

  .category-pagination {
    display: block;
    margin-top: 18px;
    padding-top: 14px;
  }

  .category-pagination__summary,
  .category-pagination__actions {
    display: none;
  }

  .category-pagination__mobile {
    align-items: center;
    display: flex;
    justify-content: space-between;
  }

  .category-pagination__mobile span {
    color: var(--dashboard-muted);
    font-size: 11px;
    font-weight: 800;
  }

  .category-pagination__mobile button {
    align-items: center;
    display: inline-flex;
    font-size: 15px;
    justify-content: center;
    padding: 0;
  }

  .category-detail {
    align-items: flex-end;
    padding: 0;
  }

  .category-detail__content {
    border-radius: 24px 24px 0 0;
    max-height: calc(100vh - 20px);
    max-height: calc(100dvh - max(12px, env(safe-area-inset-top)));
    padding: 14px 18px max(20px, calc(14px + env(safe-area-inset-bottom)));
    width: 100%;
  }

  .category-detail__handle {
    background: #d5d4dc;
    border-radius: 999px;
    display: block;
    height: 4px;
    margin: 0 auto 14px;
    width: 42px;
  }

  .category-detail__header h2 {
    font-size: 21px;
  }

  .category-detail__sticky-header,
  .transaction-detail__sticky-header {
    margin: -14px -4px 0;
    padding: 14px 4px 0;
    top: -14px;
  }

  .category-detail__item {
    grid-template-columns: 40px minmax(0, 1fr) auto;
  }

  .category-detail__icon {
    height: 40px;
    width: 40px;
  }

  .transaction-detail {
    align-items: flex-end;
    padding: 0;
  }

  .transaction-detail__content {
    border-radius: 24px 24px 0 0;
    max-height: calc(100vh - 20px);
    max-height: calc(100dvh - max(12px, env(safe-area-inset-top)));
    padding: 14px 16px max(20px, calc(14px + env(safe-area-inset-bottom)));
    width: 100%;
  }

  .transaction-detail__handle {
    background: #d5d4dc;
    border-radius: 999px;
    display: block;
    height: 4px;
    margin: 0 auto 14px;
    width: 42px;
  }

  .transaction-detail__header h2 { font-size: 21px; }
  .transaction-detail__amount { padding: 13px; }
  .transaction-detail__amount strong { font-size: 23px; }
  .transaction-detail__grid { gap: 8px; }
  .transaction-detail__grid > div { padding: 10px; }

  .recurring-summary strong {
    font-size: 24px;
  }
}

@media (max-width: 390px) {
  .summary-card__topline {
    font-size: 12px;
  }

  .summary-card__value {
    font-size: 36px;
  }

  .summary-card__flow-value {
    font-size: 24px;
  }

  .category-item__content {
    gap: 8px;
  }

  .recurring-item {
    gap: 8px;
  }
}

@keyframes dashboard-spin {
  to { transform: rotate(360deg); }
}

@keyframes skeleton-pulse {
  0%, 100% { opacity: 0.55; }
  50% { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .is-spinning,
  .skeleton {
    animation: none;
  }

  .category-item__track span,
  .quick-action,
  .refresh-button,
  .category-detail__item.is-clickable {
    transition: none;
  }
}
</style>

<style>
body.dark .finance-dashboard {
  --dashboard-ink: #f5f5f8;
  --dashboard-muted: #aaa9ba;
  --dashboard-surface: #29283b;
  --dashboard-soft: #343347;
  --dashboard-border: #3b394e;
}

body.dark .finance-dashboard .quick-action--primary,
body.dark .finance-dashboard .quick-action__icon,
body.dark .finance-dashboard .empty-state__icon,
body.dark .finance-dashboard .recurring-item__date {
  background: rgba(6, 180, 138, 0.14);
}

body.dark .finance-dashboard .quick-action--primary {
  border-color: rgba(6, 180, 138, 0.28);
}

body.dark .finance-dashboard .panel-heading__action {
  background: rgba(6, 180, 138, 0.16);
  border-color: rgba(102, 224, 189, 0.22);
  color: #66e0bd;
}

body.dark .finance-dashboard .category-item__fixed-badge {
  background: rgba(6, 180, 138, 0.16);
  border-color: rgba(102, 224, 189, 0.22);
  color: #66e0bd;
}

body.dark .finance-dashboard .quick-action--primary .quick-action__icon {
  background: var(--dashboard-accent);
}

body.dark .finance-dashboard .period-card__icon--week {
  background: rgba(95, 109, 242, 0.18);
}

body.dark .finance-dashboard .period-card__icon--days {
  background: rgba(242, 169, 59, 0.16);
}

body.dark .finance-dashboard .recurring-summary,
body.dark .finance-dashboard .category-item__track,
body.dark .finance-dashboard .skeleton--action {
  background: #343347;
}

body.dark .finance-dashboard .category-pagination__actions button,
body.dark .finance-dashboard .category-pagination__mobile button {
  background: #343347;
}

body.dark .finance-dashboard .dashboard-feedback--error {
  background: rgba(239, 106, 103, 0.12);
  border-color: rgba(239, 106, 103, 0.3);
  color: #ffaaa8;
}

body.dark .finance-dashboard .transaction-detail__message {
  background: rgba(6, 180, 138, 0.12);
  border-color: rgba(102, 224, 189, 0.2);
}

body.dark .finance-dashboard .transaction-detail__message.is-empty {
  background: var(--dashboard-soft);
  border-color: var(--dashboard-border);
}
</style>
